import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DatePickerInput } from 'src/core/POS/DateSelector';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useGetIsPurchaseIdsInUsedForVendorId } from 'src/hooks/useGetIsPurchaseIdsInUsedForVendorId';
import { ContentId } from 'src/utils/constants/contentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchasingIdInUseResultType } from 'src/WebApiController';

import { PurchaseVendorAccountSelectionInput } from '../../../common/Purchase/PurchaseVendorAccountSelectionInput';
import { PurchaseVendorSelectionInput } from '../../../common/Purchase/PurchaseVendorSelectionInput';
import { PurchaseOrderFormDataType } from '../../MultiplePurchaseWizard';

export const PurchaseOrderSectionHeader = ({
  poIndex,
}: {
  poIndex: number;
}) => {
  const { register, watch, setValue, formState, clearErrors, setError } =
    useFormContext<PurchaseOrderFormDataType>();

  const sectionValues = watch(`purchaseOrders.${poIndex}`);
  const { purchaseDate, vendorOrderId, vendor } = sectionValues;

  const vendorOrderIdPlaceholder = useContent(
    ContentId.EnterPurchaseOrderIdFromVendor
  );

  const orderIdUsedErrorMessage = useContent(ContentId.OrderIdInUseSameVendor);
  const [orderIdBlurred, setOrderIdBlurred] = useState<string>();
  const { purchasingIdInUseQuery } = useGetIsPurchaseIdsInUsedForVendorId(
    orderIdBlurred,
    vendor?.value?.id
  );

  const formStateErrors = formState.errors.purchaseOrders?.[poIndex];

  useEffect(() => {
    if (
      purchasingIdInUseQuery.data ===
      PurchasingIdInUseResultType.SameOrderIdAndVendorId
    ) {
      setError(`purchaseOrders.${poIndex}.vendorOrderId`, {
        message: orderIdUsedErrorMessage,
      });
    } else {
      if (formStateErrors?.vendorOrderId?.message === orderIdUsedErrorMessage) {
        clearErrors(`purchaseOrders.${poIndex}.vendorOrderId`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasingIdInUseQuery.data]);

  const requiredMsg = useContent(ContentId.Required);

  return (
    <Stack direction="column">
      <Stack gap={'xxl'} style={{ marginBottom: vars.spacing.m }}>
        <PosFormField
          label={<Content id={ContentId.Website} />}
          errors={formStateErrors?.vendor?.message}
        >
          <PurchaseVendorSelectionInput<PurchaseOrderFormDataType>
            fieldName={`purchaseOrders.${poIndex}.vendor`}
            style={{ width: '100%' }}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.Account} />}>
          <PurchaseVendorAccountSelectionInput<PurchaseOrderFormDataType>
            vendorFieldName={`purchaseOrders.${poIndex}.vendor`}
            fieldName={`purchaseOrders.${poIndex}.vendorAccount`}
            partialFieldName={`purchaseOrders.${poIndex}.partialVendorAccount`}
            style={{ width: '100%' }}
          />
        </PosFormField>
        <PosFormField
          label={<Content id={ContentId.ExternalPurchaseId} />}
          errors={formStateErrors?.vendorOrderId?.message}
        >
          <PosTextField
            value={vendorOrderId?.value || undefined}
            placeholder={vendorOrderIdPlaceholder}
            spellCheck={false}
            {...register(`purchaseOrders.${poIndex}.vendorOrderId.value`, {
              validate: {
                required: (fieldVal) => {
                  return fieldVal?.length ? undefined : requiredMsg;
                },
              },

              onChange: (event) => {
                if (event.target.value) {
                  clearErrors(`purchaseOrders.${poIndex}.vendorOrderId`);
                }
                setValue(
                  `purchaseOrders.${poIndex}.vendorOrderId.hasChanged`,
                  true
                );
              },
            })}
            onBlur={(e) => setOrderIdBlurred(e.target.value)}
            trimOnBlur={true}
          />
        </PosFormField>
        <PosFormField
          label={<Content id={ContentId.PurchaseDate} />}
          errors={formStateErrors?.purchaseDate?.message}
        >
          <DatePickerInput
            date={
              purchaseDate?.value ? new Date(purchaseDate.value) : undefined
            }
            onDateChange={(date: Date) => {
              if (date) {
                clearErrors(`purchaseOrders.${poIndex}.purchaseDate`);
                const dateStr = date.toISOString();
                if (dateStr !== purchaseDate?.value) {
                  setValue(
                    `purchaseOrders.${poIndex}.purchaseDate`,
                    posChangedField(dateStr)
                  );
                }
              }
            }}
            locale={getLocaleFromLanguageOrCurrent()}
          />
        </PosFormField>
      </Stack>
    </Stack>
  );
};
